import { DealRoomDisabledSection, DealRoomSection } from '@app/constants/enums/deal-room';
import { IDealRoomSectionContent } from '@app/interfaces/deal-room';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './SectionNavBar.styles';
import { Tooltip } from 'antd';
import openingImg from 'assets/images/landing/opening.svg';
import insuranceCoverageSharedBuildingImg from 'assets/images/landing/insurance-coverage-shared-building.svg';
import electricChargingStationImg from 'assets/images/landing/electric-charging-station.svg';
import quoteImg from 'assets/images/landing/quote.svg';
import listenToOurPodcastImg from 'assets/images/landing/listen-to-our-podcasts.svg';
import attachmentImg from 'assets/images/landing/attachments.svg';
import signUpForInsuranceImg from 'assets/images/landing/sign-up-for-insurance.svg';
import comparisonImg from 'assets/images/landing/comparison.svg';

const COMPARISON_ICON = <img src={comparisonImg} alt="Comparison" />;

export const sectionIconMap: Record<string, React.ReactNode> = {
  [DealRoomSection.OPENING]: <img src={openingImg} alt="Opening" />,
  [DealRoomSection.INSURANCE_COVERAGE_SHARED_BUILDING]: (
    <img src={insuranceCoverageSharedBuildingImg} alt="insuranceCoverageSharedBuildingImg" />
  ),
  [DealRoomSection.ELECTRIC_CHARGING_STATION]: (
    <img src={electricChargingStationImg} alt="electricChargingStationImg" />
  ),
  [DealRoomSection.COMPARISONS]: COMPARISON_ICON,
  [DealRoomSection.DOCUMENTS]: <img src={quoteImg} alt="listenToOurPodcastImg" />,
  [DealRoomSection.LISTEN_TO_OUR_PODCASTS]: <img src={listenToOurPodcastImg} alt="attachmentImg" />,
  [DealRoomSection.ATTACHMENTS]: <img src={attachmentImg} alt="listenToOurPodcastImg" />,
  [DealRoomSection.DECIDED_TO_PURCHASE_THE_INSURANCE]: <img src={signUpForInsuranceImg} alt="signUpForInsuranceImg" />,
};

export const sectionNameMap: Record<string, string> = {
  [DealRoomSection.OPENING]: 'dealRooms.landing.headerSections.opening',
  [DealRoomSection.INSURANCE_COVERAGE_SHARED_BUILDING]:
    'dealRooms.landing.headerSections.insuranceCoverageSharedBuilding',
  [DealRoomSection.ELECTRIC_CHARGING_STATION]: 'dealRooms.landing.headerSections.electricChargingStation',
  [DealRoomSection.COMPARISONS]: 'dealRooms.sections.comparisons',
  [DealRoomSection.DOCUMENTS]: 'dealRooms.landing.headerSections.quote',
  [DealRoomSection.LISTEN_TO_OUR_PODCASTS]: 'dealRooms.landing.headerSections.listenToOurPodcasts',
  [DealRoomSection.ATTACHMENTS]: 'dealRooms.landing.headerSections.attachments',
  [DealRoomSection.DECIDED_TO_PURCHASE_THE_INSURANCE]: 'dealRooms.landing.headerSections.signUpForInsurance',
};

type SectionIconKey = DealRoomSection | DealRoomDisabledSection | string;

export const getIcon = (section: SectionIconKey | { key: SectionIconKey }): React.ReactNode => {
  const key = typeof section === 'string' ? section : section.key;
  return key ? sectionIconMap[key] ?? <></> : <></>;
};

const getSectionName = (section: IDealRoomSectionContent): string | null => {
  return sectionNameMap[section.key] ?? null;
};

interface SectionNavBarProps {
  sections: IDealRoomSectionContent[];
  onChangeActiveKey: (key: string) => void;
}

export const SectionNavBar: React.FC<SectionNavBarProps> = ({ sections, onChangeActiveKey }) => {
  const { t } = useTranslation();

  const visibleSections = sections;

  const getLabel = (section: IDealRoomSectionContent): string => {
    const name = getSectionName(section);
    if (name) return t(name);
    if (section.label) return section.label;
    if (section.isExternal) return section.labelKey;
    return t(section.labelKey);
  };

  return (
    <S.NavBarWrapper>
      <S.ScrollContainer>
        {visibleSections.map((section) => (
          <Tooltip key={section.key} title={getLabel(section)}>
            <S.PillButton onClick={() => onChangeActiveKey(section.key)}>
              <S.PillLabel>{getLabel(section)}</S.PillLabel>
              <S.PillIcon>{getIcon(section)}</S.PillIcon>
            </S.PillButton>
          </Tooltip>
        ))}
      </S.ScrollContainer>
    </S.NavBarWrapper>
  );
};
